/* global process */
import {
  AuthenticatedTemplate,
  MsalContext,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import * as Sentry from "@sentry/browser";
import dayjs from "dayjs";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { Component, lazy, Suspense } from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import {
  Container,
  Popover,
  PopoverBody,
  PopoverHeader,
} from "reactstrap";

import componentLoader from "../../bigFiles/componentLoader";
import Aside from "../../components/Aside/";
import {
  clearDeployWatchdogStartedAt,
  DeploySuspenseFallback,
} from "../../components/DeploySuspenseFallback";
import Footer from "../../components/Footer/";
import HeaderModernized from "../../components/Header/HeaderModernized";
import nav from "../../components/Sidebar/_nav";
import HelpSidebar from "../../components/Sidebar/HelpSidebar";
import SidebarModernized from "../../components/Sidebar/SidebarModernized";
import { isFeatureEnabled } from "../../config/featureFlags";
import { isAuthRedirectPending } from "../../helpers/authRedirectPending";
import {
  ADS_ALLOW_EXCHANGES_ROUTE_EXIT_KEY,
  getIsExchangesE2eSession,
} from "../../helpers/cypressE2eFlags";
import { getIsCypressAut } from "../../helpers/isCypressAut";
import { clearDeepLinkHistoryPair } from "../../helpers/postAuthGrace";
import { softReturnToLogin } from "../../helpers/softReturnToLogin";
import { getLegacyExchangesRedirectTarget } from "../../views/Exchanges/utils/urlUtils";
import msalInstance from "../../msalInstance";
import Dashboard from "../../views/Dashboard/";
// import WaitingLogin from "../../views/Login/WaitingLogin";

const AUTH_SPINNER_TEST_ID = "deploy-auth-spinner-fallback";
/** Slow B2C / large-account finalize can exceed the 20s Suspense deploy watchdog. */
const AUTH_SPINNER_WATCHDOG_MS = 60000;

const reportAuthSpinnerWatchdogExhausted = () => {
  Sentry.captureMessage("Auth spinner watchdog exhausted", {
    level: "warning",
    tags: {
      resourceType: "auth_spinner",
      recoveryStatus: "exhausted",
      errorType: "auth_spinner_watchdog",
    },
  });
};

/**
 * Stuck auth spinner: soft-return to #/login (no deploy Refresh UI).
 * Soft-reload of an OAuth hash was intentionally avoided (DE-9408).
 */
const handleAuthSpinnerWatchdogFire = (dispatch) => {
  reportAuthSpinnerWatchdogExhausted();
  clearDeployWatchdogStartedAt(AUTH_SPINNER_TEST_ID);
  softReturnToLogin(dispatch);
};

// Sales Orders
const SalesOrdersMainPage = lazy(() =>
  componentLoader(
    () => import("../../views/Orders.Sales/SalesOrdersMain"),
  ),
);
// Sales Orders Mockup
const SalesOrdersMockup = lazy(() =>
  componentLoader(
    () => import("../../views/Orders.Sales/SalesOrdersMockup"),
  ),
);
// Exchanges (modernized)
const ExchangesPage = lazy(() =>
  componentLoader(() => import("../../views/Exchanges/ExchangesPage")),
);
// Sidebar Style Preview
const SidebarStylePreview = lazy(() =>
  componentLoader(
    () => import("../../components/Sidebar/SidebarStylePreview"),
  ),
);
// Repairs
const PBHRepairsMainPage = lazy(() =>
  componentLoader(() => import("../../views/Orders.Repairs/")),
);

//Invoices
const InvoicesMainPage = lazy(() =>
  componentLoader(() => import("../../views/Invoices/InvoicesMain")),
);
//BI
const BIMainPage = lazy(() =>
  componentLoader(() => import("../../views/BI/BI_main")),
);
const BIConfig = lazy(() =>
  componentLoader(() => import("../../views/BI/BI_config")),
);
//Super User
const UsersMainPage = lazy(() =>
  componentLoader(() => import("../../views/Users/UsersMain")),
);

const ReceiversMainPage = lazy(() =>
  componentLoader(() => import("../../views/Internal/ReceiverRequests")),
);

const EasyJetTracking = lazy(() =>
  componentLoader(
    () => import("../../views/EasyJet/T Orders/EasyJetTrackingTOrders"),
  ),
);

const EasyJetShipLogic = lazy(() =>
  componentLoader(
    () => import("../../views/EasyJet/Shipments/EasyJetShipLogic"),
  ),
);
const Outlet = lazy(() =>
  componentLoader(() => import("../../views/Dashboard/Outlet")),
);
const Internal = lazy(() =>
  componentLoader(() => import("../../views/Internal/Internal")),
);
const PriceLine = lazy(() =>
  componentLoader(() => import("../../views/Internal/PriceLineUpdates")),
);

const PartCreator = lazy(() =>
  componentLoader(
    () => import("../../views/Internal/PartCreator/PartCreator"),
  ),
);
const FlightHours = lazy(() =>
  componentLoader(() => import("../../views/Internal/FlightHours")),
);

const ReturnAirwayBill = lazy(() =>
  componentLoader(
    () => import("../../views/Internal/ReturnAirwayBill.js"),
  ),
);

const AircraftCreation = lazy(() =>
  componentLoader(
    () => import("../../views/Internal/AircraftCreation.js"),
  ),
);

const AccountSettings = lazy(() =>
  componentLoader(() => import("../../views/Account/EmailNotifications")),
);

const ManageAccount = lazy(() =>
  componentLoader(() => import("../../views/Account/ManageAccount")),
);

const Wizard = lazy(() =>
  componentLoader(() => import("../../views/Internal/Wizard.js")),
);

const ManagePowerBi = lazy(() =>
  componentLoader(() => import("../../views/BI/ManagePowerBi.js")),
);
const Admin = lazy(() =>
  componentLoader(() => import("../../views/Admin/Admin.js")),
);

const CompanySettings = lazy(() =>
  componentLoader(() => import("../../views/Admin/CompanySettings.tsx")),
);

// Import Login to render directly when needed
const Login = lazy(() =>
  componentLoader(() => import("../../views/Login/Login")),
);
function debounce(fn, ms) {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, arguments);
    }, ms);
  };
}

// Permission helpers derived from Sidebar _nav.js
function getAllNavEntries() {
  const items = (nav && nav.items) || [];
  const entries = [];
  items.forEach((item) => {
    if (item && item.url) {
      entries.push({ url: item.url, auth: item.auth });
    }
    if (Array.isArray(item?.subMenu)) {
      item.subMenu.forEach((sub) => {
        if (sub && sub.url) {
          entries.push({ url: sub.url, auth: sub.auth });
        }
      });
    }
  });
  return entries;
}

const NAV_ENTRIES = getAllNavEntries();

function normalizePath(path) {
  try {
    const withoutHash = path.startsWith("#") ? path.slice(1) : path;
    const beforeQuery = (withoutHash.split("?")[0] || "/").trim();
    if (!beforeQuery) return "/";
    return beforeQuery.endsWith("/") && beforeQuery !== "/"
      ? beforeQuery.slice(0, -1)
      : beforeQuery;
  } catch (_e) {
    return path;
  }
}

function getRequiredAuthForPath(pathname) {
  const path = normalizePath(pathname);
  // Exact match first
  const match = NAV_ENTRIES.find((e) => e.url === path);
  if (match) return match.auth;
  // Walk up path segments to find nearest parent defined in nav
  const segments = path.split("/").filter(Boolean);
  while (segments.length > 0) {
    const candidate = "/" + segments.join("/");
    const parent = NAV_ENTRIES.find((e) => e.url === candidate);
    if (parent) return parent.auth;
    segments.pop();
  }
  return undefined; // No explicit auth requirement found
}

function userHasAuth(auth, user) {
  try {
    if (!auth) return true;
    if (!user) return false;
    const permissions = user.userPermissions || [];
    const has = (perm) =>
      permissions.some((p) => p?.permission?.name == perm) ||
      (perm == "Internal" && user?.company?.id == 2);
    if (Array.isArray(auth)) {
      return auth.every((a) => has(a));
    }
    return has(auth);
  } catch (_e) {
    return false;
  }
}

function renderLegacyExchangesRedirect({ location }) {
  return <Redirect to={getLegacyExchangesRedirectTarget(location)} />;
}

class Full extends Component {
  constructor(props) {
    super(props);
    this.state = {
      waitFilter: false,
      batchType: "",
      batchTypes: [],
      title: "",
      helpVisible: false,

      popoverOpen: false,
      bannerHeight: 0,
      width: window.innerWidth,
    };
    this.setOrders = this.setOrders.bind(this);
    this.setExchanges = this.setExchanges.bind(this);
    this.resetWaitFilter = this.resetWaitFilter.bind(this);
    this.setBatchUpdateRequest = this.setBatchUpdateRequest.bind(this);
    this.setBatchTypes = this.setBatchTypes.bind(this);
    this.toggleHelpVisible = this.toggleHelpVisible.bind(this);
    this.toggle = this.toggle.bind(this);
    this.debhandleWindowSizeChange = debounce(
      this.handleWindowSizeChange.bind(this),
      500,
    );
  }

  static contextType = MsalContext;

  toggle() {
    this.setState((prevState) => {
      return {
        popoverOpen: !prevState.popoverOpen,
      };
    });
  }

  setOrders(e) {
    e.stopPropagation();
    window.location.href = "#/orders/sales";
  }
  setExchanges(e, params) {
    e.stopPropagation();
    //prevent showing toast if we're just opening new request form
    this.setState(
      params.includes("newRequest")
        ? { waitFilter: false }
        : { waitFilter: true },
    );
    window.location.href = `#/exchanges${params}`;
  }

  resetWaitFilter() {
    this.setState({ waitFilter: false });
  }

  setBatchUpdateRequest(batchType, title, exportCols) {
    this.setState({ batchType, title, exportCols });
  }

  setBatchTypes(batchTypes) {
    this.setState({ batchTypes });
  }
  measureBannerHeight = () => {
    setTimeout(() => {
      const banner = document.querySelector(".bannerClass");
      if (banner) {
        const height = banner.offsetHeight;
        this.setState({ bannerHeight: height });
      }
    }, 0);
  };

  handleWindowSizeChange() {
    this.setState({ width: window.innerWidth });
  }

  componentDidMount() {
    if (this.props.isBannerVisible) {
      // Banner just became visible, measure its height
      this.measureBannerHeight();
    }
    window.addEventListener("resize", this.debhandleWindowSizeChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isBannerVisible && !prevProps.isBannerVisible) {
      // Banner just became visible, measure its height
      this.measureBannerHeight();
    }
    if (this.state.width !== prevState.width) {
      this.measureBannerHeight();
    }
    if (!prevProps.helpDataAll && this.props.helpDataAll) {
      this.toggleHelpVisible(true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debhandleWindowSizeChange);
  }

  toggleHelpVisible(bool) {
    this.setState({ helpVisible: bool });
    if (!bool) {
      this.props.setHelpDataAll(false);
    }
  }

  render() {
    let user = this.props.user;
    const { authentication } = this.props;

    // Extract design variants from URL query params (all routes now use modernized UI)
    const currentPath = window.location.hash || window.location.pathname;
    let headerVariant = "bold";
    let sidebarVariant = "dark";
    let sidebarStyle = "option3";

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = currentPath.includes("?")
        ? new URLSearchParams(currentPath.split("?")[1])
        : new URLSearchParams();

      headerVariant =
        urlParams.get("header") || hashParams.get("header") || "bold";
      sidebarVariant =
        urlParams.get("sidebar") || hashParams.get("sidebar") || "dark";
      sidebarStyle =
        urlParams.get("style") || hashParams.get("style") || "option3";

      const validHeaderVariants = ["minimal", "bold", "gradient", "split"];
      const validSidebarVariants = ["dark", "light", "brand", "two-tone"];

      if (!validHeaderVariants.includes(headerVariant))
        headerVariant = "bold";
      if (!validSidebarVariants.includes(sidebarVariant))
        sidebarVariant = "dark";
    } catch (e) {
      headerVariant = "bold";
      sidebarVariant = "dark";
      sidebarStyle = "option3";
    }

    const isCypress = getIsCypressAut();
    const isExchangesE2eSession = getIsExchangesE2eSession();

    // First check if we're still authenticating (ignore MSAL "startup")
    const inProgress = this.context?.inProgress;
    const isMsalInteractive =
      inProgress === "login" ||
      inProgress === "acquireToken" ||
      inProgress === "ssoSilent" ||
      inProgress === "handleRedirect";
    // Treat presence of MSAL auth fragments in the hash as an authenticating phase
    // to avoid briefly rendering Login before MSAL kicks in after redirect.
    const hasMsalAuthFragment = (function () {
      try {
        const h = window.location.hash || "";
        // covers both our hashRouter prefix and raw fragments
        return (
          /[?#](code=|id_token=|state=)/i.test(h) ||
          h.startsWith("#/state=")
        );
      } catch (_e) {
        return false;
      }
    })();

    // After B2C redirect, MSAL strips the auth hash before LOGIN_SUCCESS + finalize
    // complete. Redux also resets on reload, so emailLoggingIn is not reliable.
    // If we treat that gap as "not authenticating", we Redirect to /login and break
    // the code exchange. Hold a spinner until Redux has the profile.
    let hasMsalAccountAwaitingReduxUser = false;
    try {
      hasMsalAccountAwaitingReduxUser =
        !isCypress &&
        !isExchangesE2eSession &&
        !authentication?.loggingOut &&
        msalInstance.getAllAccounts().length > 0 &&
        !this.props.user;
    } catch (_e) {
      hasMsalAccountAwaitingReduxUser = false;
    }

    const authRedirectPending =
      !isCypress && !isExchangesE2eSession && isAuthRedirectPending();

    const isAuthenticating =
      authentication?.msalLoggingIn ||
      authentication?.emailLoggingIn ||
      isMsalInteractive ||
      hasMsalAuthFragment ||
      hasMsalAccountAwaitingReduxUser ||
      authRedirectPending;

    // Diagnostic: log MSAL inProgress state during interactive phases
    try {
      if (inProgress && inProgress !== "none") {
        console.info(
          "AuthFlowDebug: inProgress",
          inProgress,
          "hash",
          window.location.hash,
        );
      }
    } catch (_e) {
      /* ignore diagnostics errors */
    }

    // Diagnostics removed after debugging

    // If authentication is in progress and no Redux user yet, always show a spinner
    // to avoid briefly flashing the Login page between redirect and waiting screen.
    // Watchdog: stuck OAuth / silent hang → soft-return to login (ADSWEBUI-REACT-38B).
    if (isAuthenticating && !this.props.user) {
      return (
        <DeploySuspenseFallback
          className="mainSpinner"
          testId={AUTH_SPINNER_TEST_ID}
          timeoutMs={AUTH_SPINNER_WATCHDOG_MS}
          persistDeadlineAcrossRemounts
          onWatchdogFire={() =>
            handleAuthSpinnerWatchdogFire(this.props.dispatch)
          }
        />
      );
    }

    // Left auth spinner successfully — do not keep a half-spent deadline.
    clearDeployWatchdogStartedAt(AUTH_SPINNER_TEST_ID);

    // Only exchanges E2E should pin the AUT to /exchanges when the hash hits
    // Sales or inventory hash during exchanges E2E: pin AUT to /exchanges.
    if (isExchangesE2eSession) {
      const currentHash = String(window.location.hash || "");
      let allowRouteExit = false;
      try {
        allowRouteExit =
          sessionStorage.getItem(ADS_ALLOW_EXCHANGES_ROUTE_EXIT_KEY) ===
          "1";
      } catch (_e) {
        // ignore storage errors in tests
      }
      if (
        !allowRouteExit &&
        /^#\/(inventory|salesmockup|orders\/sales)\b/i.test(currentHash)
      ) {
        try {
          clearDeepLinkHistoryPair();
        } catch (_e) {
          // ignore storage errors in tests
        }
        window.location.hash = "#/exchanges";
        return (
          <div className="mainSpinner">
            <ProgressSpinner />
          </div>
        );
      }
    }

    const appShell = (
      <div className="app">
        {/* Cypress-only banner removed after debugging */}
        {this.props.isBannerVisible && (
          <Message
            severity="info"
            style={{ position: "sticky", top: "0", zIndex: "1000" }}
            className="w-100 bannerClass"
            content={
              <div
                className="w-100"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ width: "calc(100vw - 100px)" }}>
                  <i
                    className="pi pi-info-circle mr-2"
                    style={{ marginRight: "5px" }}
                  />
                  <span style={{ fontSize: "13px" }}>
                    On{" "}
                    {dayjs(process.env.REACT_APP_2FA_DATE).format(
                      "DD MMMM YYYY",
                    )}
                    , we will be implementing two-factor authentication for
                    all users. For more information about how this might
                    affect you, please click &apos;More Info&apos;. If you
                    have any further questions or concerns, please contact
                    your system administrator.
                  </span>
                  <Button
                    label="More Info"
                    id="More-2FA-info"
                    style={{
                      display: "inline-block",
                      height: "19px",
                      fontSize: "14px",
                      padding: "2px",
                      marginLeft: "10px",
                    }}
                    className="p-button-text p-button-plain"
                    onClick={this.toggle}
                    aria-label="More-2FA-info"
                  />
                  <Popover
                    placement="bottom"
                    isOpen={this.state.popoverOpen}
                    target="More-2FA-info"
                    toggle={this.toggle}
                  >
                    <PopoverHeader>
                      Two-Factor Authentication (2FA)
                    </PopoverHeader>
                    <PopoverBody>
                      <h5
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "larger",
                        }}
                      >
                        Why are we implementing 2FA?
                      </h5>
                      <br />
                      Two-factor authentication (2FA) significantly
                      enhances account security by ensuring that even if
                      your password is compromised, unauthorized users
                      cannot access your account without also having access
                      to your mobile device. This provides additional
                      security against unauthorized access and potential
                      data breaches.
                      <br />
                      <h5
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "larger",
                        }}
                      >
                        How will this affect you?
                      </h5>
                      <br />
                      The first time you log in after 2FA has been
                      implemented, you will need to set up a phone number
                      that will be used to send you a unique verification
                      code. There will be an option to either receive this
                      by SMS or a phone call. Your registered phone number
                      will then be used to verify you for all subsequent
                      logins, so you will need to use your phone to receive
                      a code to log in.
                      <br />
                    </PopoverBody>
                  </Popover>
                </div>
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-plain"
                  onClick={() => {
                    this.setState({ popoverOpen: false });
                    this.props.handleBannerDismiss();
                  }}
                  aria-label="Dismiss"
                />
              </div>
            }
          />
        )}
        <HeaderModernized
          designVariant={headerVariant}
          toggleHelpVisible={this.toggleHelpVisible}
          setHelpDataAll={this.props.setHelpDataAll}
          bannerHeight={this.state.bannerHeight}
          bannerVisible={this.props.isBannerVisible}
        />
        <div className="app-body">
          <SidebarModernized
            {...this.props}
            designVariant={sidebarVariant}
            compactStyle={sidebarStyle}
            bannerVisible={this.props.isBannerVisible}
            bannerHeight={this.state.bannerHeight}
          />
          <main
            className="main"
            style={{
              ...(window.location.href.includes("#/pickslips")
                ? { backgroundColor: "var(--ajw-purple)" }
                : {}),
              ...(this.props.isBannerVisible && this.state.bannerHeight > 0
                ? {
                    minHeight: `calc(75vh - ${this.state.bannerHeight}px)`,
                  }
                : {}),
            }}
          >
            <Container
              fluid
              style={
                window.location.href.includes("#/pickslips")
                  ? { backgroundColor: "var(--ajw-purple)" }
                  : {}
              }
            >
              <Suspense
                fallback={
                  <div className="mainSpinner">
                    <ProgressSpinner />
                  </div>
                }
              >
                <Switch>
                  {this.props.user &&
                    (!this.props.user.userPermissions ||
                      this.props.user.userPermissions.length === 0 ||
                      !this.props.user.teamUsers ||
                      this.props.user.teamUsers.length === 0) && (
                      <Route
                        path="/dashboard"
                        render={() => <Redirect to="/holding" />}
                      />
                    )}
                  {
                    // Only show dashboard route if the user has permissions to prevent navigation via direct link -
                    // all other pages except outlet will redirect to /holding automatically if user has no permissions
                    this.props.user &&
                      (!this.props.user.userPermissions ||
                        this.props.user.userPermissions.length === 0 ||
                        !this.props.user.teamUsers ||
                        this.props.user.teamUsers.length === 0) && (
                        <>
                          <Route
                            path="/orders/pbhnew"
                            render={renderLegacyExchangesRedirect}
                          />
                          <Route
                            path="/orders/adhoc"
                            name="AdHoc Orders"
                            component={PBHRepairsMainPage}
                          />
                          {/* 
                          <Route
                            path="/quoterequests"
                            render={(props) => (
                              <QuotesMainPage
                                {...props}
                                fromHolding={false}
                              />
                            )}
                          /> 
                          */}
                          <Route
                            path="/quotes"
                            render={(props) => (
                              <RaisedQuotesMainPage {...props} />
                            )}
                          />
                          {/* Consignment route - component not implemented
                          <Route
                            path="/orders/consignment"
                            name="Consignment"
                            component={ConsignmentMainPage}
                          />
                          */}
                          <Route
                            path="/orders/rfq"
                            name="RFQ Orders"
                            component={SalesOrdersMainPage}
                          />
                          <Route
                            path="/users"
                            name="Users"
                            component={UsersMainPage}
                          />
                          <Route
                            path="/shipments/shipments"
                            name="Shipments"
                            component={SalesOrdersMainPage}
                          />
                          <Route
                            path="/inventory/mystock"
                            name="My Stock"
                            component={SalesOrdersMainPage}
                          />
                          <Route
                            path="/invoices"
                            name="Invoices"
                            component={InvoicesMainPage}
                          />
                          <Route
                            path="/eventory"
                            name="Outlet"
                            component={Outlet}
                          />
                          <Route
                            path="/bi/config"
                            name="biConfig"
                            component={BIConfig}
                          />
                          <Route
                            path="/bi"
                            name="bi"
                            component={BIMainPage}
                          />
                          <Route
                            path="/easyjetTracking"
                            name="EasyJet Tracking"
                            component={EasyJetTracking}
                          />
                          {isFeatureEnabled(
                            "shipmentView",
                            this?.props?.user?.id,
                          ) && (
                            <Route
                              path="/easyJetTrackingShipments"
                              name="EasyJet Tracking Shipments"
                              component={EasyJetShipLogic}
                            />
                          )}
                          <Route
                            path="/receivers"
                            name="Receivers"
                            component={ReceiversMainPage}
                          />
                          <Route
                            path="/priceline"
                            name="PriceLine"
                            component={PriceLine}
                          />
                          <Route
                            path="/partCreator"
                            name="Part Creator"
                            component={PartCreator}
                          />
                          <Route
                            path="/flightHours"
                            name="Flight Hours"
                            component={FlightHours}
                          />
                          {/* CustomsEntry route - component not implemented
                          <Route
                            path="/customsEntry"
                            name="CustomsEntryRequests"
                            component={CustomsEntryRequests}
                          />
                          */}
                          <Route
                            path="/returnAirwayBill"
                            name="Return Airway Bill"
                            component={ReturnAirwayBill}
                          />
                          <Route
                            path="/aircraftCreation"
                            name="Aircraft Creation"
                            component={AircraftCreation}
                          />
                          <Route
                            path="/wizard"
                            render={(props) => (
                              <Wizard
                                {...props}
                                batchType={this.state.batchType}
                                batchTypes={this.state.batchTypes}
                                title={this.state.title}
                                exportCols={this.state.exportCols}
                                setBatchTypes={this.setBatchTypes}
                              />
                            )}
                          />
                          <Route
                            path="/serviceBulletin"
                            render={(props) => (
                              <ServiceBulletin {...props} user={user} />
                            )}
                          />
                          <Route
                            path="/manageAccount"
                            render={(props) => (
                              <ManageAccount {...props} user={user} />
                            )}
                          />
                          <Route
                            path="/emailNotifications"
                            render={(props) => (
                              <AccountSettings {...props} user={user} />
                            )}
                          />
                          <Route
                            path="/managePowerBi"
                            name="ManagePowerBi"
                            component={ManagePowerBi}
                          />
                          <Route
                            path="/admin"
                            name="Admin"
                            component={Admin}
                          />
                          <Route
                            path="/companySettings"
                            name="Company Settings"
                            component={CompanySettings}
                          />
                          {
                            // Only show internal route if company id is 2
                            this.props.user &&
                              this.props.user.company &&
                              this.props.user.company.id &&
                              this.props.user.company.id === 2 && (
                                <Route
                                  path="/internal"
                                  render={(props) => (
                                    <Internal
                                      {...props}
                                      setBatchUpdateRequest={
                                        this.setBatchUpdateRequest
                                      }
                                      setBatchTypes={this.setBatchTypes}
                                    />
                                  )}
                                />
                              )
                          }
                          <Redirect
                            exact
                            from="/orders"
                            to="/orders/sales"
                          />
                          <Redirect from="/users" to="/users" />
                          <Redirect
                            from="/shipments"
                            to="/shipments/shipments"
                          />
                          <Redirect
                            from="/inventory"
                            to="/inventory/mystock"
                          />
                          <Route
                            path="/orders/sales"
                            render={(props) => (
                              <SalesOrdersMainPage {...props} />
                            )}
                          />
                          <Route
                            exact
                            path="/salesmockup"
                            render={(props) => (
                              <SalesOrdersMockup {...props} />
                            )}
                          />
                          <Route
                            path="/exchanges"
                            render={(props) => (
                              <ExchangesPage {...props} />
                            )}
                          />
                          <Route
                            path="/exchangesmockup"
                            render={renderLegacyExchangesRedirect}
                          />
                          <Route
                            path="/sidebar-preview"
                            render={(props) => (
                              <SidebarStylePreview {...props} />
                            )}
                          />
                        </>
                      )
                  }
                  {this.props.user &&
                    this.props.user.userPermissions &&
                    this.props.user.userPermissions.length > 0 &&
                    this.props.user.teamUsers &&
                    this.props.user.teamUsers.length > 0 && (
                      <Route
                        path="/dashboard"
                        render={(props) => (
                          <Dashboard
                            {...props}
                            setOrders={this.setOrders}
                            setExchanges={this.setExchanges}
                            resetWaitFilter={this.resetWaitFilter}
                          />
                        )}
                      />
                    )}

                  <Route
                    path="/orders/sales"
                    render={(props) => <SalesOrdersMainPage {...props} />}
                  />
                  <Route
                    path="/orders/pbhnew"
                    render={renderLegacyExchangesRedirect}
                  />
                  <Route
                    path="/orders/pbnew"
                    render={renderLegacyExchangesRedirect}
                  />
                  <Route
                    exact
                    path="/salesmockup"
                    render={(props) => <SalesOrdersMockup {...props} />}
                  />
                  <Route
                    path="/exchanges"
                    render={(props) => <ExchangesPage {...props} />}
                  />
                  <Route
                    path="/exchangesmockup"
                    render={renderLegacyExchangesRedirect}
                  />
                  <Route
                    path="/sidebar-preview"
                    render={(props) => <SidebarStylePreview {...props} />}
                  />

                  {/* Modernized UI Routes - Uses modernized header/sidebar with normal content */}
                  {this.props.user &&
                    this.props.user.userPermissions &&
                    this.props.user.userPermissions.length > 0 &&
                    this.props.user.teamUsers &&
                    this.props.user.teamUsers.length > 0 && (
                      <Route
                        path="/modernized"
                        render={(props) => (
                          <Dashboard
                            {...props}
                            setOrders={this.setOrders}
                            setExchanges={this.setExchanges}
                            resetWaitFilter={this.resetWaitFilter}
                          />
                        )}
                      />
                    )}
                  {/* Redirect old /orders/pbh route to modernized exchanges */}
                  <Route
                    path="/orders/pbh"
                    render={renderLegacyExchangesRedirect}
                  />

                  <Route
                    path="/orders/adhoc"
                    name="AdHoc Orders"
                    component={PBHRepairsMainPage}
                  />
                  <Route
                    path="/orders/rfq"
                    name="RFQ Orders"
                    component={SalesOrdersMainPage}
                  />
                  <Route
                    path="/users"
                    name="Users"
                    component={UsersMainPage}
                  />
                  <Route
                    path="/shipments/shipments"
                    name="Shipments"
                    component={SalesOrdersMainPage}
                  />
                  <Route
                    path="/inventory/mystock"
                    name="My Stock"
                    component={SalesOrdersMainPage}
                  />
                  <Route
                    path="/invoices"
                    name="Invoices"
                    component={InvoicesMainPage}
                  />
                  <Route
                    path="/eventory"
                    name="Outlet"
                    component={Outlet}
                  />
                  <Route
                    path="/bi/config"
                    name="biConfig"
                    component={BIConfig}
                  />
                  <Route path="/bi" name="bi" component={BIMainPage} />

                  <Route
                    path="/easyjetTracking"
                    name="EasyJet Tracking"
                    component={EasyJetTracking}
                  />

                  {isFeatureEnabled(
                    "shipmentView",
                    this?.props?.user?.id,
                  ) && (
                    <Route
                      path="/easyJetTrackingShipments"
                      name="EasyJet Tracking Shipments"
                      component={EasyJetShipLogic}
                    />
                  )}
                  <Route
                    path="/receivers"
                    name="Receivers"
                    component={ReceiversMainPage}
                  />

                  <Route
                    path="/priceline"
                    name="PriceLine"
                    component={PriceLine}
                  />

                  <Route
                    path="/partCreator"
                    name="Part Creator"
                    component={PartCreator}
                  />

                  <Route
                    path="/flightHours"
                    name="Flight Hours"
                    component={FlightHours}
                  />

                  <Route
                    path="/returnAirwayBill"
                    name="Return Airway Bill"
                    component={ReturnAirwayBill}
                  />

                  <Route
                    path="/aircraftCreation"
                    name="Aircraft Creation"
                    component={AircraftCreation}
                  />

                  <Route
                    path="/wizard"
                    render={(props) => (
                      <Wizard
                        {...props}
                        batchType={this.state.batchType}
                        batchTypes={this.state.batchTypes}
                        title={this.state.title}
                        exportCols={this.state.exportCols}
                        setBatchTypes={this.setBatchTypes}
                      />
                    )}
                  />

                  <Route
                    path="/manageAccount"
                    render={(props) => (
                      <ManageAccount {...props} user={user} />
                    )}
                  />

                  <Route
                    path="/emailNotifications"
                    render={(props) => (
                      <AccountSettings {...props} user={user} />
                    )}
                  />

                  <Route
                    path="/managePowerBi"
                    name="ManagePowerBi"
                    component={ManagePowerBi}
                  />

                  <Route path="/admin" name="Admin" component={Admin} />

                  <Route
                    path="/companySettings"
                    name="Company Settings"
                    component={CompanySettings}
                  />

                  {
                    //only show dashboard route if the user has permissions to prevent them navigating here through a link- all other pages bar outlet will redirect to /holding automatically if user has no permissions
                    this.props.user &&
                      this.props.user.company &&
                      this.props.user.company.id &&
                      this.props.user.company.id == 2 && (
                        <Route
                          path="/internal"
                          render={(props) => (
                            <Internal
                              {...props}
                              setBatchUpdateRequest={
                                this.setBatchUpdateRequest
                              }
                              setBatchTypes={this.setBatchTypes}
                            />
                          )}
                        />
                      )
                  }

                  <Redirect exact from="/orders" to="/orders/sales" />

                  <Redirect from="/shipments" to="/shipments/shipments" />
                  <Redirect from="/inventory" to="/inventory/mystock" />

                  {(() => {
                    // Only after authentication is complete, check user permissions
                    if (this.props.user) {
                      if (
                        this.props.user.userPermissions &&
                        this.props.user.userPermissions.length > 0 &&
                        this.props.user.teamUsers &&
                        this.props.user.teamUsers.length > 0
                      ) {
                        return <Redirect from="/" to="/dashboard" />;
                      } else {
                        return <Redirect from="/" to="/holding" />;
                      }
                    }

                    // If no user and not authenticating:
                    // Avoid redirecting to /login if we're in the middle of an MSAL redirect handoff
                    // where the URL may contain auth params (code/id_token/state). In that case,
                    // stay put and let msal-react/WaitingLogin continue the flow to prevent flicker.
                    try {
                      const href = window.location.href || "";
                      const msalAuthParams =
                        /(\?|#)(code=|id_token=|state=)/i.test(href);
                      if (
                        msalAuthParams ||
                        hasMsalAccountAwaitingReduxUser ||
                        authRedirectPending
                      ) {
                        return null;
                      }
                    } catch (_e) {
                      /* ignore */
                    }

                    return <Redirect from="/" to="/login" />;
                  })()}
                </Switch>
              </Suspense>
            </Container>
          </main>
          <Aside />
        </div>
        <Footer
          toggleHelpVisible={this.toggleHelpVisible}
          helpData={this.props.helpData}
        />
        <HelpSidebar
          helpVisible={this.state.helpVisible}
          toggleHelpVisible={this.toggleHelpVisible}
          helpData={this.props.helpData}
        />
      </div>
    );

    // Guard: if navigating to a route that requires permissions the user lacks,
    // redirect to the most appropriate landing: dashboard (if permitted) else holding.
    // We inspect the current hash path produced by HashRouter.
    // Skip under Cypress: E2E seeds auth via password grant; MSAL/Identity timing can
    // briefly disagree with this guard and send tests to /holding before the UI mounts.
    try {
      if (!isCypress) {
        const currentHash = window.location.hash || "";
        // Remove the leading '#'
        const currentPath = currentHash.startsWith("#")
          ? currentHash.slice(1)
          : currentHash;
        const currentPathNoQuery = normalizePath(currentPath);
        const requiredAuth = getRequiredAuthForPath(currentPathNoQuery);
        const authedForCurrent = userHasAuth(requiredAuth, user);

        if (user && requiredAuth && !authedForCurrent) {
          // Decide fallback target
          const dashboardAuth = getRequiredAuthForPath("/dashboard");
          const canSeeDashboard = userHasAuth(dashboardAuth, user);
          const target = canSeeDashboard ? "#/dashboard" : "#/holding";
          if (!currentHash.startsWith(target)) {
            window.location.hash = target;
            // Render a small spinner while redirecting
            return (
              <div className="mainSpinner">
                <ProgressSpinner />
              </div>
            );
          }
        }
      }
    } catch (_e) {
      // if guard fails, proceed to normal render
    }

    return (
      <div>
        {isCypress || this.props.user ? (
          appShell
        ) : (
          <>
            <AuthenticatedTemplate>{appShell}</AuthenticatedTemplate>
            <UnauthenticatedTemplate>
              {(() => {
                const inProgress = this.context?.inProgress;
                const isMsalInteractive =
                  inProgress === "login" ||
                  inProgress === "acquireToken" ||
                  inProgress === "ssoSilent" ||
                  inProgress === "handleRedirect";
                const isAuthenticating =
                  authentication?.msalLoggingIn ||
                  authentication?.emailLoggingIn ||
                  isMsalInteractive;

                if (isAuthenticating) {
                  return (
                    <DeploySuspenseFallback
                      className="mainSpinner"
                      testId={AUTH_SPINNER_TEST_ID}
                      timeoutMs={AUTH_SPINNER_WATCHDOG_MS}
                      persistDeadlineAcrossRemounts
                      onWatchdogFire={() =>
                        handleAuthSpinnerWatchdogFire(this.props.dispatch)
                      }
                    />
                  );
                }

                clearDeployWatchdogStartedAt(AUTH_SPINNER_TEST_ID);

                if (this.props.user) {
                  if (
                    this.props.user.userPermissions &&
                    this.props.user.userPermissions.length > 0 &&
                    this.props.user.teamUsers &&
                    this.props.user.teamUsers.length > 0
                  ) {
                    return <Redirect from="/" to="/dashboard" />;
                  } else {
                    return <Redirect from="/" to="/holding" />;
                  }
                }

                return (
                  <Suspense fallback={<ProgressSpinner />}>
                    <Login />
                  </Suspense>
                );
              })()}
            </UnauthenticatedTemplate>
          </>
        )}
      </div>
    );
  }
}

//export default Full;

function mapStateToProps(state) {
  const { authentication } = state;
  const { user } = authentication;

  return {
    user,
    authentication,
  };
}

const connectedFull = connect(mapStateToProps)(Full);
export default connectedFull;
